import classNames from "classnames";
import PropTypes from "prop-types";
import RootCloseWrapper from "react-overlays/lib/RootCloseWrapper";
import { Component, cloneElement } from "react";
import {
  bsClass, getClassSet, prefix, splitBsPropsAndOmit,
} from "./utils/bootstrapUtils";
import createChainedFunction from "./utils/createChainedFunction";
import ValidComponentChildren from "./utils/ValidComponentChildren";

const propTypes = {
  open: PropTypes.bool,
  pullRight: PropTypes.bool,
  onClose: PropTypes.func,
  labelledBy: PropTypes.oneOfType([
    PropTypes.string, PropTypes.number,
  ]),
  onSelect: PropTypes.func,
  rootCloseEvent: PropTypes.oneOf(["click", "mousedown"]),
};

const defaultProps = {
  bsRole: "menu",
  pullRight: false,
};

class DropdownMenu extends Component {
  menuElement: HTMLElement | null = null;

  constructor(props: any) {
    super(props);

    this.handleRootClose = this.handleRootClose.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  getFocusableMenuItems() {
    if (!this.menuElement) {
      return [];
    }

    return Array.from(this.menuElement.querySelectorAll("[tabIndex=\"-1\"]"));
  }

  getItemsAndActiveIndex() {
    const items = this.getFocusableMenuItems();
    const activeIndex = items.indexOf(document.activeElement);

    return { items, activeIndex };
  }

  focusNext() {
    const { items, activeIndex } = this.getItemsAndActiveIndex();
    if (items.length === 0) {
      return;
    }

    const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;

    items[nextIndex].focus();
  }

  focusPrevious() {
    const { items, activeIndex } = this.getItemsAndActiveIndex();
    if (items.length === 0) {
      return;
    }

    const prevIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;

    items[prevIndex].focus();
  }

  handleKeyDown(event: any) {
    switch (event.keyCode) {
    case 40: // keycode.codes.down:
      this.focusNext();
      event.preventDefault();
      break;
    case 37: // keycode.codes.up:
      this.focusPrevious();
      event.preventDefault();
      break;
    case 27: // keycode.codes.esc:
    case 9:// keycode.codes.tab:
      this.props.onClose(event, { source: "keydown" });
      break;
    default:
    }
  }

  handleRootClose(event: any) {
    this.props.onClose(event, { source: "rootClose" });
  }

  render() {
    const {
      open,
      pullRight,
      labelledBy,
      onSelect,
      className,
      rootCloseEvent,
      children,
      ...props
    } = this.props;

    const [bsProps, elementProps] = splitBsPropsAndOmit(props, ["onClose"]);

    const classes = {
      ...getClassSet(bsProps),
      [prefix(bsProps, "right")]: pullRight,
    };

    return (
      <RootCloseWrapper
        disabled={!open}
        onRootClose={this.handleRootClose}
        event={rootCloseEvent}
      >

        <ul
          {...elementProps}
          ref={(el) => { this.menuElement = el; }}
          role="menu"
          className={classNames(className, classes)}
          aria-labelledby={labelledBy}
        >

          {ValidComponentChildren.map(children, (child: any) => cloneElement(child, {
            onKeyDown: createChainedFunction(
              child.props.onKeyDown, this.handleKeyDown,
            ),
            onSelect: createChainedFunction(
              child.props.onSelect, onSelect,
            ),
          }))}
        </ul>
      </RootCloseWrapper>
    );
  }
}

DropdownMenu.propTypes = propTypes;
DropdownMenu.defaultProps = defaultProps;

export default bsClass("dropdown-menu", DropdownMenu);
